/* Inline lucide-style icons — parity with design ecxa-layout.jsx */

import type { ReactNode } from "react";

export function IconBox({
  children,
  size = 16,
  className = "",
}: {
  children: ReactNode;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  );
}

export const Icons = {
  Home: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </IconBox>
  ),
  ChartBar: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 16v-4" />
      <path d="M12 16V8" />
      <path d="M17 16v-6" />
    </IconBox>
  ),
  // Trend line, not bars: `ChartBar` already belongs to Consumo, and two
  // adjacent admin entries with the same glyph are two entries nobody can tell
  // apart in the sidebar.
  ChartLine: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="m7 15 4-4 3 3 5-6" />
      <path d="M15 8h4v4" />
    </IconBox>
  ),
  Coins: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
      <path d="M7 6h1v4" />
      <path d="m16.71 13.88.7.71-2.82 2.82" />
    </IconBox>
  ),
  Inbox: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </IconBox>
  ),
  Messages: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </IconBox>
  ),
  Bot: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M12 8V4H8" />
      <path d="M8 8h8a4 4 0 0 1 0 8H8a4 4 0 0 1 0-8zM10 13v.01M14 13v.01M6 8H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 8h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" />
    </IconBox>
  ),
  Radio: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
    </IconBox>
  ),
  Users: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M9 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM16 3.13a4 4 0 0 1 0 7.75" />
    </IconBox>
  ),
  Store: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M2 7l4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
      <path d="m4 12 1.5 7.5A2 2 0 0 0 7.46 21h9.08a2 2 0 0 0 1.96-1.5L20 12M2 7h20M12 2v5" />
    </IconBox>
  ),
  Building: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M10 6h4M10 10h4M10 14h4M10 18h4" />
    </IconBox>
  ),
  TestTube: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2M8.5 2h7M14.5 16h-5" />
    </IconBox>
  ),
  Settings: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </IconBox>
  ),
  Sun: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </IconBox>
  ),
  Moon: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </IconBox>
  ),
  Search: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </IconBox>
  ),
  ChevronDown: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="m6 9 6 6 6-6" />
    </IconBox>
  ),
  Shield: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </IconBox>
  ),
  User: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </IconBox>
  ),
  LogOut: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </IconBox>
  ),
  Lock: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </IconBox>
  ),
  Unlock: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </IconBox>
  ),
  Plus: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M12 5v14M5 12h14" />
    </IconBox>
  ),
  Menu: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </IconBox>
  ),
  ArrowLeft: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </IconBox>
  ),
  ChevronRight: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="m9 18 6-6-6-6" />
    </IconBox>
  ),
  ChevronLeft: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="m15 18-6-6 6-6" />
    </IconBox>
  ),
  /** Vertical drag grip (resize handle affordance). */
  GripVertical: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="9" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="19" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="19" r="1" fill="currentColor" stroke="none" />
    </IconBox>
  ),
  PanelRight: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M15 3v18" />
    </IconBox>
  ),
  StickyNote: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3z" />
      <path d="M15 3v6h6" />
    </IconBox>
  ),
  ExternalLink: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
    </IconBox>
  ),
  Wrench: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </IconBox>
  ),

  Download: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </IconBox>
  ),
  List: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </IconBox>
  ),
  Grid: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
    </IconBox>
  ),
  MoreVertical: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </IconBox>
  ),
  Edit: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </IconBox>
  ),
  Copy: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M4 16V6a2 2 0 0 1 2-2h10" />
    </IconBox>
  ),
  Trash: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </IconBox>
  ),
  Save: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8M7 3v5h8" />
    </IconBox>
  ),
  Play: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M8 5v14l11-7-11-7Z" />
    </IconBox>
  ),
  Check: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M20 6 9 17l-5-5" />
    </IconBox>
  ),
  Eye: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </IconBox>
  ),
  EyeOff: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </IconBox>
  ),
  AlertTriangle: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </IconBox>
  ),
  HelpCircle: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </IconBox>
  ),
  Clock: (s = 16, c = "") => (
    <IconBox size={s} className={c}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </IconBox>
  ),
};
